/**
 * A server-sent-events reader for a `fetch` response, because EventSource can
 * only GET and the chat stream is a POST with a body.
 *
 * The parser is pure: text goes in as it arrives, split wherever the network
 * split it, and each complete event's `data` comes out. Comment lines (`: ping`)
 * keep proxies awake and are dropped; several `data:` lines in one event are
 * joined with a newline, as the spec says; other fields are ignored.
 */
export class SseParser {
  private buffer = '';
  private data: string[] = [];

  /** Feeds a chunk; returns the data of every event it completed. */
  push(chunk: string): string[] {
    this.buffer += chunk;
    const events: string[] = [];
    let newline = this.nextNewline();
    while (newline !== -1) {
      const line = this.buffer.slice(0, newline).replace(/\r$/, '');
      this.buffer = this.buffer.slice(newline + 1);
      const event = this.line(line);
      if (event !== undefined) events.push(event);
      newline = this.nextNewline();
    }
    return events;
  }

  /** The stream ended: an event still open without its blank line is delivered. */
  flush(): string[] {
    const rest = this.buffer === '' ? [] : this.push('\n');
    return [...rest, ...this.push('\n')];
  }

  private nextNewline(): number {
    return this.buffer.indexOf('\n');
  }

  private line(line: string): string | undefined {
    if (line === '') {
      if (this.data.length === 0) return undefined;
      const event = this.data.join('\n');
      this.data = [];
      return event;
    }
    if (line.startsWith(':')) return undefined;
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    if (field !== 'data') return undefined;
    const value = colon === -1 ? '' : line.slice(colon + 1);
    this.data.push(value.startsWith(' ') ? value.slice(1) : value);
    return undefined;
  }
}

/** Reads a response body to its end, handing each event's data over as it completes. */
export async function readSse(
  body: ReadableStream<Uint8Array>,
  onData: (data: string) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  const parser = new SseParser();
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      for (const data of parser.push(decoder.decode(value, { stream: true }))) onData(data);
    }
    for (const data of parser.push(decoder.decode())) onData(data);
    for (const data of parser.flush()) onData(data);
  } finally {
    reader.releaseLock();
  }
}
